import { FLAGS } from '@iheartjane/feature-flags';
import { initMoengage, useMoengageIdentify } from '@iheartjane/tracking';
import InnerHTML from 'dangerously-set-html-content';
import isEmpty from 'lodash-es/isEmpty';
import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import { ExternalScripts } from 'remix-utils/external-scripts';
import { useShouldHydrate } from 'remix-utils/use-should-hydrate';

import { AgeGate } from '@jane/bloom/components/ageGate/ageGate';
import { AppMode } from '@jane/bloom/components/appMode';
import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { ClientEnv } from '@jane/bloom/components/document/clientEnv';
import { Footer } from '@jane/bloom/components/document/footer';
import {
  BodyContent,
  HeadScripts,
} from '@jane/bloom/components/document/googleTagPlatform';
import { AppWrappers } from '@jane/bloom/components/document/wrappers';
import { Jdid } from '@jane/bloom/components/jdid';
import { StoreId } from '@jane/bloom/components/storeId';
import { useBodyScrubber } from '@jane/bloom/hooks/useBodyScrubber';
import { useJdidSync } from '@jane/bloom/hooks/useJdidSync';
import { useMixpanelRegistration } from '@jane/bloom/hooks/useMixpanelRegistration';
import { useMixpanelReidentify } from '@jane/bloom/hooks/useMixpanelReidentify';
import { useResetCartScript } from '@jane/bloom/hooks/useResetCartScript';
import { useTrackPageView } from '@jane/bloom/hooks/useTrackPageView';
import { buildThemeCssVars } from '@jane/bloom/resources/theme';
import type { LocalRootLoaderData } from '@jane/bloom/types/loaders/rootLoader';

export const Document = ({
  ageGate,
  appConfig: { datadog, mixpanelProjectToken, moengageWorkspaceId },
  buildVersion,
  customLanguage,
  featureFlags,
  googleSsoClientId,
  googleTagPlatform,
  jdid,
  menuUrl,
  nodeEnv,
  operator,
  partnerScripts,
  pinnedStore,
  pluginVersion,
  publicPath,
  theme,
  whoami,
}: PropsWithChildren<LocalRootLoaderData>) => {
  const includeRemixScripts = useShouldHydrate();
  const pinnedStoreId = pinnedStore?.id;
  const themeInlineCss = useMemo(() => buildThemeCssVars(theme), [theme]);

  // Runs before registration so a stale anonymous id is cleared first.
  useMixpanelReidentify({
    enabled: featureFlags?.[FLAGS.c2BloomMixpanelReidentify] ?? false,
    isGuest: whoami.user.id == null,
    jdid,
  });

  useMixpanelRegistration({
    buildVersion,
    featureFlags,
    operatorId: operator.id,
    operatorName: operator.name,
    pluginVersion,
    projectToken: mixpanelProjectToken,
    storeCity: pinnedStore?.city,
    storeId: pinnedStore?.id,
    storeName: pinnedStore?.name,
    storeState: pinnedStore?.state,
    userId: whoami.user.id?.toString(),
  });

  useJdidSync(jdid);

  initMoengage({ workspaceId: moengageWorkspaceId });
  useMoengageIdentify(whoami.user);

  useResetCartScript();
  useBodyScrubber();
  useTrackPageView();

  return (
    <html
      lang="en"
      // eslint-disable-next-line react/no-unknown-property
      prefix="og: http://ogp.me/ns# fb: http://ogp.me/ns/fb# product: http://ogp.me/ns/product#"
    >
      <head>
        <ClientEnv
          values={{
            buildVersion,
            datadog,
            jdid,
            menuUrl,
            mixpanelProjectToken,
            nodeEnv,
            publicPath,
          }}
        />
        <HeadScripts {...googleTagPlatform} />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
        <style>{themeInlineCss}</style>
      </head>
      <body id="app">
        {partnerScripts
          .filter(
            ({ storeIds }) =>
              isEmpty(storeIds) ||
              !pinnedStoreId ||
              storeIds?.includes(pinnedStoreId)
          )
          .map(({ script }) => (
            <InnerHTML
              key={script}
              /**
               * 'dangerously-set-html-content' is used here instead of vanilla React's
               * `dangerouslySetInnerHTML` because any JS included in the script
               * will be executed.
               */
              html={script}
            />
          ))}
        <BodyContent {...googleTagPlatform} />
        <AppWrappers
          customLanguage={customLanguage}
          googleSsoClientId={googleSsoClientId}
        >
          <Outlet />

          <ClientOnly>
            {() => (
              <AgeGate
                initiallyOpen={!ageGate?.confirmedAge}
                type={ageGate?.type}
              />
            )}
          </ClientOnly>
          <Footer />
        </AppWrappers>

        <Jdid jdid={jdid} />
        <AppMode />
        {pinnedStoreId && <StoreId storeId={pinnedStoreId} />}

        <ScrollRestoration />
        {includeRemixScripts && <Scripts />}
        <ClientOnly>{() => <ExternalScripts />}</ClientOnly>
      </body>
    </html>
  );
};
